import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import { 
  Building2, 
  Globe, 
  Factory, 
  Sofa, 
  Ship, 
  Award,
  CheckCircle,
  ArrowRight
} from 'lucide-react';
import InteractiveDivisionSelector from '../components/InteractiveDivisionSelector';
import RoomBasedBrowsing from '../components/RoomBasedBrowsing';
import BeforeAfterShowcase from '../components/BeforeAfterShowcase';
// import ExportCountriesMap from '../components/ExportCountriesMap';

const Home = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const stats = [
    { label: 'Years of Excellence', value: 15, suffix: '+' },
    { label: 'Projects Completed', value: 500, suffix: '+' },
    { label: 'Countries Served', value: 25, suffix: '+' }
  ];

  const divisions = [
    {
      icon: Factory,
      title: 'Manufacturing',
      description: 'State-of-the-art automated facility with CNC machining and PU finishing capabilities.',
      image: 'https://images.pexels.com/photos/14805033/pexels-photo-14805033.jpeg?auto=compress&cs=tinysrgb&w=1260',
      link: '/manufacturing'
    },
    {
      icon: Sofa,
      title: 'Furniture & Interiors',
      description: 'Premium modular furniture and turnkey interior solutions for residential and commercial spaces.',
      image: 'https://images.unsplash.com/photo-1751591148854-49c2835dfaa1?crop=entropy&cs=srgb&fm=jpg&q=85',
      link: '/furniture'
    },
    {
      icon: Ship,
      title: 'Export Division',
      description: 'Global trade in coconut fibre and coco peat with world-class logistics and quality standards.',
      image: 'https://images.pexels.com/photos/9694390/pexels-photo-9694390.jpeg?auto=compress&cs=tinysrgb&w=1260',
      link: '/export'
    }
  ];

  const features = [
    { icon: Award, title: 'ISO Certified', description: 'International quality standards' },
    { icon: Factory, title: 'Modern Facility', description: 'Automated manufacturing processes' },
    { icon: Globe, title: 'Global Reach', description: 'Serving 25+ countries worldwide' },
    { icon: CheckCircle, title: 'Quality Assured', description: 'Rigorous quality control protocols' }
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1760072513442-9872656c1b07?crop=entropy&cs=srgb&fm=jpg&q=85)'
          }}
        />
        <div className="absolute inset-0 hero-overlay" />
        
        <div className="relative z-10 max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
          >
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-none mb-6 text-shadow" data-testid="hero-title">
              Manufacturing Excellence.<br />
              Global Trade.<br />
              Intelligent Interiors.
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-white/90 mb-8 max-w-3xl mx-auto">
              From automated furniture manufacturing to international exports and turnkey interiors — LMR IMPEX delivers precision without compromise.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link 
                to="/contact" 
                data-testid="hero-cta-quote"
                className="bg-white text-slate-900 hover:bg-slate-100 h-12 px-8 uppercase tracking-widest text-xs font-bold transition-all inline-flex items-center"
              >
                Request Quote
              </Link>
              <Link 
                to="/furniture" 
                data-testid="hero-cta-explore"
                className="border border-white text-white hover:bg-white hover:text-slate-900 h-12 px-8 uppercase tracking-widest text-xs font-bold transition-all inline-flex items-center"
              >
                Explore Products
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-slate-50 py-16" data-testid="stats-section">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
                data-testid={`stat-${index}`}
              >
                <div className="text-5xl md:text-6xl font-bold text-slate-900 mb-2">
                  {isVisible && <CountUp end={stat.value} duration={2.5} suffix={stat.suffix} />}
                </div>
                <div className="text-sm font-bold uppercase tracking-widest text-muted-foreground">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Division Selector */}
      <InteractiveDivisionSelector />

      {/* Company Snapshot */}
      <section className="py-24 md:py-32" data-testid="company-snapshot">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl"
          >
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">Who We Are</p>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-6">
              Precision Manufacturing Meets Global Trade Excellence
            </h2>
            <p className="text-lg md:text-xl leading-relaxed text-muted-foreground mb-8">
              LMR IMPEX operates across multiple verticals—modular furniture manufacturing, luxury interiors, 
              strategic imports from China, and international exports of coconut products. With our modern 
              automated facility and unwavering commitment to quality, we serve international buyers, developers, 
              architects, and premium customers worldwide.
            </p>
            <Link 
              to="/about" 
              data-testid="about-link"
              className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-slate-900 hover:text-bronze-500 transition-colors group"
            >
              Learn More About Us
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Divisions */}
      <section className="bg-slate-50 py-24 md:py-32" data-testid="divisions-section">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">Our Divisions</p>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight">
              Integrated Solutions Across Industries
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {divisions.map((division, index) => (
              <motion.div
                key={division.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                data-testid={`division-${index}`}
              >
                <Link to={division.link} className="group block">
                  <div className="bg-white border border-slate-200 overflow-hidden transition-all duration-500 hover:shadow-lg">
                    <div className="relative h-64 overflow-hidden">
                      <img 
                        src={division.image} 
                        alt={division.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-slate-900/20 group-hover:bg-slate-900/30 transition-colors duration-500" />
                    </div>
                    <div className="p-8">
                      <division.icon className="w-10 h-10 text-bronze-500 mb-4" />
                      <h3 className="text-2xl font-medium mb-3">{division.title}</h3>
                      <p className="text-muted-foreground leading-relaxed mb-4">{division.description}</p>
                      <span className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-slate-900 group-hover:text-bronze-500 transition-colors">
                        Explore
                        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 md:py-32" data-testid="why-choose-us">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">Why Choose LMR IMPEX</p>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight">
              Excellence in Every Detail
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
                data-testid={`feature-${index}`}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-50 mb-4">
                  <feature.icon className="w-8 h-8 text-slate-900" />
                </div>
                <h3 className="text-xl font-medium mb-2">{feature.title}</h3>
                <p className="text-muted-foreground text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Room-Based Furniture Browsing */}
      <RoomBasedBrowsing />

      {/* Before/After Interior Transformations */}
      <BeforeAfterShowcase />

      {/* Export Countries Map - Coming Soon */}
      {/* <ExportCountriesMap /> */}

      {/* CTA Section */}
      <section className="bg-slate-900 text-white py-24" data-testid="cta-section">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-6">
              Ready to Start Your Project?
            </h2>
            <p className="text-lg md:text-xl leading-relaxed text-white/80 mb-8 max-w-2xl mx-auto">
              Get in touch with our team to discuss how LMR IMPEX can bring your vision to life.
            </p>
            <Link 
              to="/contact" 
              data-testid="cta-contact-button"
              className="inline-flex items-center bg-bronze-500 text-white hover:bg-bronze-600 h-12 px-8 uppercase tracking-widest text-xs font-bold transition-all"
            >
              Contact Us Today
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
